import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import ReceptionistAppointmentCalendar from "../Common/ReceptionistAppointmentCalendar";
import Swal from "sweetalert2";
import {
  handleSuccess,
  showErrorAlert,
} from "../../../services/ErrorHandlingService";

const ReceptionistRescheduleModal = ({
  show,
  onHide,
  patientId,
  doctorId,
  appointmentId,
  currentAppointmentData,
  onRescheduleSuccess,
}) => {
  // Debug logging
  console.log("ReceptionistRescheduleModal props:", {
    show,
    patientId,
    doctorId,
    appointmentId,
    currentAppointmentData,
  });

  // Check if this is a new booking (for queue patients without appointment_id)
  const isNewBooking = currentAppointmentData?.isNewBooking;

  // Check if this is a queue reschedule (has queue_id but no appointment_id)
  const isQueueReschedule =
    currentAppointmentData?.queue_id && !currentAppointmentData?.appointment_id;

  console.log("ReceptionistRescheduleModal checks:", {
    isNewBooking,
    isQueueReschedule,
    queue_id: currentAppointmentData?.queue_id,
    appointment_id: currentAppointmentData?.appointment_id,
  });

  // Validate that we have required data for rescheduling or new booking
  const isValidForReschedule =
    (appointmentId && patientId && currentAppointmentData) ||
    (isQueueReschedule && patientId && currentAppointmentData);
  const isValidForNewBooking =
    isNewBooking && patientId && currentAppointmentData;
  const isValid = isValidForReschedule || isValidForNewBooking;

  const [isRescheduling, setIsRescheduling] = useState(false);

  const handleRescheduleSuccess = (rescheduledData) => {
    setIsRescheduling(false);

    let successMessage;
    if (isNewBooking) {
      successMessage = "Appointment booked successfully!";
    } else if (isQueueReschedule) {
      successMessage = "Patient appointment rescheduled successfully!";
    } else {
      successMessage = "Appointment rescheduled successfully!";
    }

    handleSuccess(successMessage);

    if (onRescheduleSuccess) {
      onRescheduleSuccess(rescheduledData);
    }

    onHide();
  };

  const handleRescheduleError = (error) => {
    setIsRescheduling(false);
    let errorMessage;
    if (isNewBooking) {
      errorMessage = "Failed to book appointment. Please try again.";
    } else if (isQueueReschedule) {
      errorMessage =
        "Failed to reschedule patient appointment. Please try again.";
    } else {
      errorMessage = "Failed to reschedule appointment. Please try again.";
    }

    showErrorAlert(errorMessage);
    console.error("Reschedule/Booking error:", error);
  };

  const handleClose = () => {
    if (isRescheduling) {
      Swal.fire({
        title: isNewBooking ? "Booking in Progress" : "Reschedule in Progress",
        text: `Please wait for the ${
          isNewBooking ? "booking" : "reschedule"
        } to complete or cancel it first.`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }
    onHide();
  };

  // Show error if required data is missing
  useEffect(() => {
    if (show && !isValid) {
      Swal.fire({
        title: "Invalid Data",
        text: isNewBooking
          ? "Cannot book appointment: Missing patient information. Please select a valid patient from the queue."
          : "Cannot reschedule: Missing appointment information. Please select a valid appointment from the list.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        onHide();
      });
    }
  }, [show, isValid, isNewBooking, onHide]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton={!isRescheduling}>
        <Modal.Title>
          <div className="d-flex align-items-center">
            <i className="fas fa-calendar-alt me-2 text-primary"></i>
            {isNewBooking ? "Book New Appointment" : "Reschedule Appointment"}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="p-3 border-bottom bg-light">
          <div className="row">
            <div className="col-md-6">
              <small className="text-muted d-block">
                {isNewBooking ? "Patient Name" : "Current Appointment"}
              </small>
              <strong>
                {isNewBooking
                  ? currentAppointmentData?.name || "N/A"
                  : currentAppointmentData?.time_slot || "N/A"}
              </strong>
            </div>
            <div className="col-md-6">
              <small className="text-muted d-block">Patient ID</small>
              <strong>{patientId || "N/A"}</strong>
            </div>
          </div>
        </div>

        <div className="p-3">
          {isValid ? (
            <ReceptionistAppointmentCalendar
              patientId={patientId}
              doctorId={doctorId}
              initialAppointmentId={appointmentId}
              currentAppointmentData={currentAppointmentData}
              onRescheduleSuccess={handleRescheduleSuccess}
              onRescheduleError={handleRescheduleError}
              onRescheduleStart={() => setIsRescheduling(true)}
              onRescheduleEnd={() => setIsRescheduling(false)}
              isNewBooking={isNewBooking}
              onHide={onHide} 
            />
          ) : (
            <div className="text-center text-muted py-4">
              <i className="fas fa-exclamation-triangle fa-2x mb-3 text-warning"></i>
              <p>
                {isNewBooking
                  ? "Invalid patient data. Please select a valid patient from the queue."
                  : "Invalid appointment data. Please select a valid appointment from the list."}
              </p>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isRescheduling}
        >
          {isRescheduling ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {isNewBooking ? "Booking..." : "Rescheduling..."}
            </>
          ) : (
            "Cancel"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ReceptionistRescheduleModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  doctorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  appointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentAppointmentData: PropTypes.object,
  onRescheduleSuccess: PropTypes.func,
};

export default ReceptionistRescheduleModal;
